//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { setUserSettings } from '@apis/user-settings/userSettingsApi';
import IUserSettings from '@models/IUserSettings';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';

export function useSetCurrentUserSettingsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (settings: Partial<IUserSettings>) =>
      setUserSettings(settings),
    onMutate: async (newSettings) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: [
          QueryKeyConstants.AccountSettings,
          QueryKeyConstants.AccountSettingsCurrentUser,
        ],
      });

      // Optimistically update to the new value
      queryClient.setQueryData(
        [
          QueryKeyConstants.AccountSettings,
          QueryKeyConstants.AccountSettingsCurrentUser,
        ],
        (old: IUserSettings | undefined) => ({
          ...(old ?? {}),
          ...newSettings,
        }),
      );
    },
    // Please notice that we are invalidating the query as part of onSuccess instead of onSettled
    // and we are not reverting the optimistically updated value in case of errors. - the reason
    // for that is because, in case of errors, we want VibeAnnouncement to re-appear after the stale
    // time of 5 minutes or on a new session (refresh)
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [
          QueryKeyConstants.AccountSettings,
          QueryKeyConstants.AccountSettingsCurrentUser,
        ],
      });
    },
  });
}
